import { historicalDetailError } from './detail-resources.js';
import { TrafficCallsChart, TrafficStatusChart } from './dense-traffic-bars.jsx';
import CallerFilter from './caller-filter.jsx';
// Traffic 分析（Phase 2 v2）
//
// 这版相比 v1 的核心变化是「调用日志」从一个简单表格升级到带筛选 + 翻页 +
// 排序的真正面板：
//   - 顶部过滤器：endpoint / status / method / country / caller / URL 搜索
//   - 列：时间 / 方法 / 状态 / 端点 / 调用方 / 延迟 / 花费 / 地区 / IP
//   - 翻页：cursor 模式（与后端 /apis/:apiId/logs 对齐）
//   - 行点击：弹抽屉，调 /logs/:logId 拿 4 个 truncated 详情
//
// 数据源：精确日期 /apis/:apiId/traffic 返回全部汇总面板，
// /callers/insights 提供当前窗口的收益记录榜单；调用日志单独走
// /apis/:apiId/logs 的 REST 游标分页，避免一次加载全部事实。

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { XShell } from '../../shell.jsx';
import { XApi } from '../../services.js';
import { exactComparisonBounds } from './analytics-window.js';
import { XPrim, XIcons } from '../../primitives.jsx';
import { Dialog, DialogHeader, DialogBody } from '../../ui/index.js';
import {
  KpiCard,
  KpiGrid,
  RangePicker,
  Section,
  ChartGrid,
  LineChart,
  HorizontalStack,
  PieSlices,
  EmptyBarChart,
  EmptyPie,
  EmptyTable,
  LoadingHint,
  FilterBar,
  PillGroup,
  Select,
  SearchInput,
  ExportCsvButton,
  SortHeader,
  StatusCodePill,
  MethodPill,
  CountryCell,
  DataTable,
  TableFooter,
  fmtNum,
  fmtPct,
  fmtUsd,
  fmtTinyUsd,
  fmtBytes,
  rangeToDays,
  rangeToBounds,
} from './analytics-shared.jsx';

const STATUS_BUCKET_OPTIONS = [
  { v: null, l: '全部' },
  { v: '2xx', l: '2xx' },
  { v: '4xx', l: '4xx' },
  { v: '5xx', l: '5xx' },
];
const METHOD_OPTIONS = [
  { v: null, l: '全部' },
  { v: 'GET', l: 'GET' },
  { v: 'POST', l: 'POST' },
  { v: 'PUT', l: 'PUT' },
  { v: 'DELETE', l: 'DEL' },
];
const PAGE_SIZE = 50;

const GRAN_OPTIONS = [{ v: 'hour', l: '小时' }, { v: 'day', l: '日' }];
const STATUS_VIEW_OPTIONS = [{ v: 'all', l: '全部' }, { v: 'success', l: '成功' }, { v: 'failed', l: '失败' }];
const EXPAND_TITLE = { calls: '调用量时序', latency: '延迟时序', status: '成功 / 失败时序' };

// 错误责任方（与 usage_records.errorOrigin 对齐）。UPSTREAM = provider 自己该关注的
// 可靠性问题；USER/REQUEST = 消费者侧(余额/模型未配/请求格式错)，非 provider 的锅。
const ORIGIN_META = {
  UPSTREAM: { label: '上游', desc: '上游可靠性（你的锅）', color: '#c0392b', bg: 'rgba(192,57,43,.14)' },
  REQUEST:  { label: '请求', desc: '请求格式错（消费者侧）', color: '#e08e0b', bg: 'rgba(224,142,11,.14)' },
  USER:     { label: '账户', desc: '余额/模型未配（消费者侧）', color: 'var(--fg-3)', bg: 'rgba(140,150,170,.14)' },
  GATEWAY:  { label: '网关', desc: '网关内部错误', color: '#b884e0', bg: 'rgba(184,132,224,.16)' },
};
const OriginBadge = ({ origin }) => {
  const m = ORIGIN_META[origin];
  if (!m) return null;
  return (
    <span title={m.desc} style={{
      fontSize: 9, padding: '1px 5px', borderRadius: 3, whiteSpace: 'nowrap',
      background: m.bg, color: m.color,
    }}>{m.label}</span>
  );
};

// 小分段切换（粒度 / 成功失败视图通用）
const SegToggle = ({ options, value, onChange }) => (
  <div style={{ display: 'inline-flex', gap: 4 }}>
    {options.map((o) => (
      <button
        key={String(o.v)}
        type="button"
        onClick={() => onChange(o.v)}
        style={{
          padding: '3px 8px', border: '1px solid var(--bd-1)',
          background: value === o.v ? 'var(--bg-3)' : 'transparent',
          color: value === o.v ? 'var(--fg-0)' : 'var(--fg-3)',
          borderRadius: 4, fontSize: 10, cursor: 'pointer',
        }}
      >{o.l}</button>
    ))}
  </div>
);

// 放大按钮 —— 打开单图完整弹窗
const ExpandBtn = ({ onClick }) => (
  <button type="button" className="btn btn-sm btn-ghost btn-icon" title="放大查看（含范围 / 过滤）" onClick={onClick}>
    <XIcons.IconMaximize size={12} />
  </button>
);

export const TrafficSection = ({ api }) => {
  const { authed, user } = XShell.useAuth();
  const owner = user?._raw?.id ?? user?.id ?? null;
  return <TrafficState key={JSON.stringify([Boolean(authed), owner, api?.id])} api={authed ? api : null} />;
};

const TrafficState = ({ api }) => {
  const { navigate } = XShell.useRoute();
  const apiId = api?.id;
  const [range, setRange] = useState('30d');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const bounds = useMemo(() => exactComparisonBounds(range, customRange), [range, customRange.from, customRange.to]);
  const selectionKey = JSON.stringify(bounds);
  const [granularity, setGranularity] = useState('day');
  const [statusView, setStatusView] = useState('all'); // 成功/失败时序：all | success | failed
  const [expanded, setExpanded] = useState(null);       // 放大弹窗：null | 'calls' | 'latency' | 'status'

  // 切换时间范围时给一个合理的默认粒度:24h → 小时,更长 → 天(用户仍可手动切)。
  const handleRangeChange = (r) => { setDrillWindow(null); setActiveLog(null); setRange(r); setGranularity(r === '1d' ? 'hour' : 'day'); };

  // 概览数据
  const [detail, setDetail] = useState(null);
  const [apiSummary, setApiSummary] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [statusCodes, setStatusCodes] = useState(null);
  const [latencyBreakdown, setLatencyBreakdown] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [errorGroups, setErrorGroups] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState(null);
  const [overviewReload, setOverviewReload] = useState(0);

  // 日志表 — 独立 state，过滤变化只重拉日志不重拉概览
  const [filters, setFilters] = useState({
    endpointId: null,
    statusBucket: null,
    statusCode: null,
    method: null,
    country: null,
    callerId: null,
  });
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [logSort, setLogSort] = useState({ key: 'createdAt', dir: 'desc' });
  const [logs, setLogs] = useState([]);
  const [logCursor, setLogCursor] = useState(null);
  const [logHasMore, setLogHasMore] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [activeLog, setActiveLog] = useState(null);
  // A8: 时序柱下钻 — 点击后强制覆盖 from/to 而不是用 range，让日志面板只显示
  // 该时段。null = 没下钻。
  const [drillWindow, setDrillWindow] = useState(null); // { from, to, label }
  const logsAnchorRef = useRef(null);
  const [logsError, setLogsError] = useState(null);
  const logFlight = useRef(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const effectiveDrill = drillWindow?.selectionKey === selectionKey ? drillWindow : null;
  const logBounds = useMemo(() => bounds ? { from: effectiveDrill?.from ?? bounds.from, to: effectiveDrill?.to ?? bounds.to } : null, [bounds, effectiveDrill]);
  const logKey = JSON.stringify([apiId, logBounds, filters, searchDebounced]);
  const currentLogKey = useRef(logKey);
  currentLogKey.current = logKey;

  // All windowed panels use the same exact bounds; legacy days endpoints
  // remain available to their other consumers.
  useEffect(() => {
    let cancelled = false;
    setDetail(null); setApiSummary(null); setTimeseries(null); setStatusCodes(null);
    setLatencyBreakdown(null); setAiUsage(null); setErrorGroups(null); setOverviewError(null);
    if (!apiId || !bounds) { setOverviewLoading(false); if (apiId) setOverviewError('日期范围必须完整、正序且不超过 365 天（UTC）'); return; }
    setOverviewLoading(true);
    XApi.providerDashboardService.trafficAnalysis(apiId, { ...bounds, granularity }).then((analysis) => {
      if (cancelled) return;
      if (!analysis) throw Error('unavailable');
      setDetail(analysis.detail); setApiSummary(analysis.apiSummary ? {...analysis.apiSummary, comparison: analysis.comparison} : null); setTimeseries(analysis.timeseries);
      setStatusCodes(analysis.statusCodes); setLatencyBreakdown(analysis.latencyBreakdown);
      setAiUsage(analysis.aiUsage ? { ...analysis.aiUsage, byModel: analysis.aiUsage.byModelExact ?? analysis.aiUsage.byModel } : null); setErrorGroups(analysis.errorGroups);
    }).catch(e => { if (!cancelled) setOverviewError(historicalDetailError(e) ?? (e?.message?.includes('UTC') ? e.message : '流量统计加载失败')); })
      .finally(() => { if (!cancelled) setOverviewLoading(false); });
    return () => { cancelled = true; };
  }, [apiId, bounds, granularity, overviewReload]);

  // 搜索框 debounce
  useEffect(() => {
    const h = setTimeout(() => setSearchDebounced(search.trim()), 300);
    return () => clearTimeout(h);
  }, [search]);

  // 拉日志（依赖所有 filter + range + drillWindow；从第一页开始）
  // drillWindow 优先：点击时序柱后，from/to 覆盖 range
  // C4: 36 字符的 UUID 形态 → 走 logId 精确匹配，否则当 URL 模糊搜索
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const fetchLogs = useCallback(async ({ append = false, cursor = null } = {}) => {
    if (!apiId || !logBounds || logFlight.current?.key === logKey) return;
    const flight = { key: logKey };
    logFlight.current = flight;
    const current = () => mounted.current && currentLogKey.current === logKey && logFlight.current === flight;
    setLogsLoading(true); setLogsError(null);
    const isUuid = UUID_RE.test(searchDebounced || '');
    try {
      const res = await XApi.providerDashboardService.apiLogs(apiId, {
        ...logBounds, scope: 'rest', limit: PAGE_SIZE, cursor: cursor || undefined,
        statusBucket: filters.statusBucket || undefined, statusCode: filters.statusCode ?? undefined, endpointId: filters.endpointId || undefined,
        country: filters.country || undefined, callerId: filters.callerId || undefined, method: filters.method || undefined,
        ...(isUuid ? { logId: searchDebounced } : { search: searchDebounced || undefined }),
      });
      if (!current()) return;
      setLogs(prev => append ? [...prev, ...res.items] : res.items);
      setLogCursor(res.nextCursor); setLogHasMore(Boolean(res.nextCursor));
    } catch {
      if (current()) setLogsError('调用日志加载失败');
    } finally {
      if (current()) { setLogsLoading(false); logFlight.current = null; }
    }
  }, [apiId, logKey, logBounds, filters, searchDebounced]);

  useEffect(() => {
    // Invalidate request identity even when an earlier query has the same key
    // after A -> invalid bounds -> A. Its old append must not block/refill A.
    logFlight.current = null;
    setLogs([]); setLogCursor(null); setLogHasMore(false); setActiveLog(null); setLogsError(null);
    if (!logBounds || !apiId) { setLogsLoading(false); return; }
    fetchLogs();
  }, [fetchLogs]);

  const onTimeseriesBarClick = useCallback(point => {
    if (!point?.ts || !bounds) return;
    const bucketStart = +new Date(point.ts);
    const from = new Date(Math.max(bucketStart, +new Date(bounds.from)));
    const to = new Date(Math.min(bucketStart + (granularity === 'hour' ? 3600_000 : 86400_000) - 1, +new Date(bounds.to)));
    if (!Number.isFinite(+from) || +from > +to) return;
    setActiveLog(null);
    setDrillWindow({ from: from.toISOString(), to: to.toISOString(), label: from.toISOString() + ' — ' + to.toISOString(), selectionKey });
    logsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [bounds, granularity, selectionKey]);

  // 客户端排序（仅作用于已加载的页）
  const sortedLogs = useMemo(() => {
    const arr = [...logs];
    const { key, dir } = logSort;
    arr.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string' && typeof bv === 'string') {
        return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return dir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [logs, logSort]);

  const onSort = (key) => {
    setLogSort((s) => ({
      key,
      dir: s.key === key ? (s.dir === 'asc' ? 'desc' : 'asc') : 'desc',
    }));
  };

  const updateFilter = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const resetFilters = () => {
    setSearch(''); setSearchDebounced('');
    setFilters({ endpointId: null, statusBucket: null, statusCode: null, method: null, country: null, callerId: null });
  };


  const comparisonAvailable = Boolean(apiSummary &&
    (!apiSummary.comparison || apiSummary.comparison.status === 'available') &&
    Number.isFinite(apiSummary.prevTotalCalls) && Number.isFinite(apiSummary.prevSuccessRate));
  const totalCalls = apiSummary?.totalCalls ?? 0;
  const successRatePct = apiSummary?.successRate ?? 0;
  const uniqueCallers = apiSummary?.uniqueCallers ?? 0;
  const avgLatency = apiSummary?.avgLatency ?? null;
  const p50Latency = apiSummary?.p50Latency ?? null;
  const p95Latency = apiSummary?.p95Latency ?? null;
  // D3 — 带宽
  const totalReqBytes = apiSummary?.totalReqBytes ?? 0;
  const totalRespBytes = apiSummary?.totalRespBytes ?? 0;
  const avgReqBytes = apiSummary?.avgReqBytes ?? null;
  const avgRespBytes = apiSummary?.avgRespBytes ?? null;
  const totalBytes = totalReqBytes + totalRespBytes;

  // C-fix: smart empty-state copy for the 3 timeseries Sections.
  // Three states:
  //   1. API has zero lifetime calls            → "首次调用后..." (the original)
  //   2. API has calls but window is empty       → tell user to widen window
  //      or switch granularity (hour ↔ day) — most common cause is hourly
  //      rollup hasn't captured a backfill yet, OR they're looking at an
  //      old API with no recent traffic.
  //   3. Window has data → no empty state (covered by chart rendering)
  const hasAnyCalls = totalCalls > 0;
  const emptyTimeseriesHint = hasAnyCalls
    ? (granularity === 'hour'
        ? '近 24 小时无可视化数据'
        : '该时间窗口内没有聚合数据')
    : '首次调用后这里会显示真实数据';
  const emptyTimeseriesSubHint = hasAnyCalls
    ? (granularity === 'hour'
        ? '切到「日」粒度看历史趋势'
        : '请切换时间范围或等待新调用')
    : '按 hour / day 切换粒度';

  // 国家 / endpoint / caller 的下拉选项 —— 从已加载日志 + 现成数据集采集
  const endpointOptions = useMemo(
    () => (detail?.endpoints || []).map((e) => ({ v: e.endpointId, l: `${e.method} ${e.name}` })),
    [detail],
  );
  const countryOptions = useMemo(() => {
    const seen = new Set();
    logs.forEach((l) => l.country && seen.add(l.country));
    return Array.from(seen).sort().map((c) => ({ v: c, l: c }));
  }, [logs]);

  const pieData = statusCodes
    ? [
        { label: '2xx', value: statusCodes.buckets?.['2xx'] || 0, color: '#22a06b' },
        { label: '3xx', value: statusCodes.buckets?.['3xx'] || 0, color: '#5b8def' },
        { label: '4xx', value: statusCodes.buckets?.['4xx'] || 0, color: '#e08e0b' },
        { label: '5xx', value: statusCodes.buckets?.['5xx'] || 0, color: '#c0392b' },
        { label: '其他 / 未收到响应', value: statusCodes.buckets?.other || 0, color: 'var(--fg-3)' },
      ]
    : [];

  const hasFilters =
    filters.endpointId || filters.statusBucket || filters.statusCode != null || filters.method || filters.country || filters.callerId || search.trim() || searchDebounced;

  // endpoint id → name lookup (for log table 端点列)
  const epLookup = useMemo(() => {
    const m = new Map();
    (detail?.endpoints || []).forEach((e) => m.set(e.endpointId, e));
    return m;
  }, [detail]);

  // 在弹窗里点柱子下钻:先关弹窗再下钻(底部日志表会过滤到该时段)。
  const drillAndClose = useCallback((point) => { setExpanded(null); onTimeseriesBarClick(point); }, [onTimeseriesBarClick]);

  // 三张时序图的渲染体,inline 与放大弹窗共用(只是 height / 下钻回调不同)。
  const renderChartBody = (kind, height, onDrill = onTimeseriesBarClick) => {
    if (overviewLoading) return <LoadingHint />;
    const pts = timeseries?.points;
    if (kind === 'calls') {
      return pts?.length
        ? <TrafficCallsChart data={pts} valueKey="calls" height={height} emptyZeroHint="该时间窗口内没有调用" onBarClick={onDrill} />
        : <EmptyBarChart height={height} hint={emptyTimeseriesHint} subHint={emptyTimeseriesSubHint} />;
    }
    if (kind === 'latency') {
      return pts?.length
        ? <LineChart data={pts} height={height} unit="ms" showPoints={pts.length <= 500} series={[
            { key: 'avgLatency', label: 'mean', color: 'var(--fg-1)' },
            { key: 'p50Latency', label: 'P50', color: '#5b8def' },
            { key: 'p95Latency', label: 'P95', color: '#e08e0b' },
          ]} />
        : <EmptyBarChart height={height} hint={hasAnyCalls ? emptyTimeseriesHint : '首次调用后这里会显示 mean / P50 / P95 三条延迟曲线'} subHint={hasAnyCalls ? emptyTimeseriesSubHint : 'P50/P95 来自所选窗口内调用记录（仅 2xx）'} />;
    }
    // status
    return pts?.length
      ? <TrafficStatusChart data={pts} height={height} show={statusView} onBarClick={onDrill} />
      : <EmptyBarChart height={height} hint={hasAnyCalls ? emptyTimeseriesHint : '首次调用后这里会显示成功 / 失败堆叠分布'} subHint={hasAnyCalls ? emptyTimeseriesSubHint : undefined} />;
  };

  if (!apiId) return null;
  return (
    <div style={{ padding: '24px 32px 64px' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16, gap: 12, flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: 'var(--fg-0)', letterSpacing: '-0.01em' }}>
            Traffic
          </h2>
          <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>
            调用量 · 成功率 · 延迟分布 · Endpoint 排行 · 原始调用日志（带筛选）
          </div>
        </div>
        <RangePicker
          value={range}
          onChange={handleRangeChange}
          customRange={customRange}
          onCustomRangeChange={(value) => { setDrillWindow(null); setActiveLog(null); setCustomRange(value); }}
        />
      </div>

      {range === '90d' && <div style={{fontSize:11,color:'var(--fg-3)',marginBottom:10}}>90 天及对比期按完整 UTC 小时对齐，包含当前正在进行的小时。</div>}
      {apiSummary?.comparison?.status === 'unavailable' && <div role="status" style={{fontSize:11,color:'var(--fg-3)',marginBottom:10}}>对比期历史不可用；当前区间统计正常显示，环比暂不展示。</div>}
      {overviewError && <div role="alert">{overviewError} {bounds && <button onClick={() => setOverviewReload(n => n + 1)}>重试统计</button>}</div>}
      <div style={{fontSize:11,color:'var(--fg-3)',marginBottom:8}}>所选日期为 UTC；调用统计含已归档的非审核 REST 历史（含免费/失败）；原始调用明细约保留 90 天，历史小时／日统计长期保留；成本不是 Provider 收益。</div>
      <KpiGrid>
        <KpiCard
          label="总调用"
          value={overviewLoading ? '…' : apiSummary ? fmtNum(totalCalls) : '—'}
          dim={!overviewLoading && totalCalls === 0}
          trend={comparisonAvailable ? { current: totalCalls, previous: apiSummary.prevTotalCalls } : null}
        />
        <KpiCard
          label="成功率"
          value={overviewLoading ? '…' : (totalCalls > 0 ? fmtPct(successRatePct / 100) : '—')}
          dim={!overviewLoading && totalCalls === 0}
          trend={
            comparisonAvailable && totalCalls > 0 && apiSummary.prevTotalCalls > 0
              ? {
                  current: successRatePct,
                  previous: apiSummary.prevSuccessRate,
                }
              : null
          }
        />
        <KpiCard label="唯一调用方" value={overviewLoading ? '…' : apiSummary ? fmtNum(uniqueCallers) : '—'} dim={!overviewLoading && uniqueCallers === 0} />
        <KpiCard
          label="均延迟"
          value={overviewLoading ? '…' : (avgLatency != null && totalCalls > 0 ? `${Math.round(avgLatency)} ms` : '—')}
          dim={!overviewLoading && totalCalls === 0}
          hint="mean，全 range"
        />
        {/* P50 / P95：所选窗口源记录的精确分位，仅 2xx。 */}
        <KpiCard
          label="P50 延迟"
          value={overviewLoading ? '…' : (p50Latency != null ? `${p50Latency} ms` : '—')}
          dim={!overviewLoading && p50Latency == null}
          hint="所选窗口，仅 2xx，中位数"
        />
        <KpiCard
          label="P95 延迟"
          value={overviewLoading ? '…' : (p95Latency != null ? `${p95Latency} ms` : '—')}
          dim={!overviewLoading && p95Latency == null}
          hint="所选窗口，仅 2xx，尾延迟"
        />
        {/* D3 — 带宽统计 */}
        <KpiCard
          label="总带宽"
          value={overviewLoading ? '…' : apiSummary ? fmtBytes(totalBytes) : '—'}
          dim={!overviewLoading && totalBytes === 0}
          hint={totalBytes > 0 ? `↑${fmtBytes(totalReqBytes)} ↓${fmtBytes(totalRespBytes)}` : '请求 + 响应'}
        />
        <KpiCard
          label="平均请求"
          value={overviewLoading ? '…' : (avgReqBytes != null ? fmtBytes(avgReqBytes) : '—')}
          dim={!overviewLoading && avgReqBytes == null}
          hint="per call"
        />
        <KpiCard
          label="平均响应"
          value={overviewLoading ? '…' : (avgRespBytes != null ? fmtBytes(avgRespBytes) : '—')}
          dim={!overviewLoading && avgRespBytes == null}
          hint="per call"
        />
      </KpiGrid>

      {!overviewError && <>
      {/* A1-A3 时序图组：调用量 / 延迟 / 成功失败。三张共享同一 timeseries
          + granularity，逻辑上是一组观察视角，宽屏下并排 3 列。
          granularity 切换只在第一张上提供，避免重复控件。 */}
      <ChartGrid minWidth={360}>
        <Section
          compact
          title="调用量时序"
          action={
            <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <SegToggle options={GRAN_OPTIONS} value={granularity} onChange={setGranularity} />
              <ExpandBtn onClick={() => setExpanded('calls')} />
            </div>
          }
        >
          {renderChartBody('calls', 160)}
          <div style={{ fontSize: 10, color: 'var(--fg-3)', marginTop: 4, textAlign: 'right' }}>
            点击柱子下钻到该时段日志 ↓
          </div>
        </Section>

        {/* A2 — 延迟时序：mean / P50 / P95 三条线。hour 粒度下 P50/P95 为 null
            只画 mean；day 粒度下三条线齐全。LineChart 会自动跳过 null 段。 */}
        <Section
          compact
          title="延迟时序"
          action={
            <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>
                mean · P50 · P95
              </span>
              <ExpandBtn onClick={() => setExpanded('latency')} />
            </div>
          }
        >
          {renderChartBody('latency', 160)}
        </Section>

        {/* A3 — 成功 / 失败堆叠柱状图。快速过滤 全部/成功/失败 + 点柱下钻到该时段日志。 */}
        <Section
          compact
          title="成功 / 失败时序"
          action={
            <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <SegToggle options={STATUS_VIEW_OPTIONS} value={statusView} onChange={setStatusView} />
              <ExpandBtn onClick={() => setExpanded('status')} />
            </div>
          }
        >
          {renderChartBody('status', 160)}
          <div style={{ fontSize: 10, color: 'var(--fg-3)', marginTop: 4, textAlign: 'right' }}>
            点击柱子下钻到该时段日志 ↓
          </div>
        </Section>
      </ChartGrid>

      {/* 单图放大弹窗 —— 更大画布 + 时间范围/粒度/成功失败 过滤;点柱仍可下钻(关弹窗后定位日志)。 */}
      {expanded && (
        <Dialog open onClose={() => setExpanded(null)} maxWidth={1120}>
          <DialogHeader title={EXPAND_TITLE[expanded]} onClose={() => setExpanded(null)} />
          <DialogBody>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
              <RangePicker
                value={range}
                onChange={handleRangeChange}
                customRange={customRange}
                onCustomRangeChange={(value) => { setDrillWindow(null); setActiveLog(null); setCustomRange(value); }}
              />
              <SegToggle options={GRAN_OPTIONS} value={granularity} onChange={setGranularity} />
              {expanded === 'status' && (
                <SegToggle options={STATUS_VIEW_OPTIONS} value={statusView} onChange={setStatusView} />
              )}
              {expanded === 'latency' && (
                <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                  mean · P50 · P95
                </span>
              )}
            </div>
            {renderChartBody(expanded, 440, drillAndClose)}
            {(expanded === 'calls' || expanded === 'status') && (
              <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 12, textAlign: 'right' }}>
                点击柱子下钻到该时段日志（关闭弹窗并定位到日志） ↓
              </div>
            )}
          </DialogBody>
        </Dialog>
      )}

      {/* A6 — AI 用量视角（仅 AI 服务渲染）：tokens / cache / tok·s / by model */}
      {aiUsage?.isAi && (
        <Section
          title="AI 用量"
          action={
            <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>
              所选 UTC 日期内的调用记录
            </span>
          }
        >
          <KpiGrid>
            <KpiCard
              label="输入 tokens"
              value={fmtNum(aiUsage.inputTokens)}
              dim={aiUsage.inputTokens === 0}
            />
            <KpiCard
              label="输出 tokens"
              value={fmtNum(aiUsage.outputTokens)}
              dim={aiUsage.outputTokens === 0}
            />
            <KpiCard
              label="cache 读"
              value={fmtNum(aiUsage.cacheReadTokens)}
              dim={aiUsage.cacheReadTokens === 0}
              hint="Anthropic cache_read / OpenAI cached"
            />
            <KpiCard
              label="cache 命中率"
              value={
                aiUsage.cacheHitRate > 0
                  ? `${(aiUsage.cacheHitRate * 100).toFixed(1)}%`
                  : '—'
              }
              dim={aiUsage.cacheHitRate === 0}
              hint="cacheRead / (input + cacheRead)"
            />
            <KpiCard
              label="平均 tok/s"
              value={aiUsage.avgTokensPerSecond != null ? `${aiUsage.avgTokensPerSecond}` : '—'}
              dim={!aiUsage.avgTokensPerSecond}
              hint="仅流式"
            />
          </KpiGrid>

          {aiUsage.byModel?.length > 0 && (
            <DataTable>
              <thead>
                <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                  <th style={{ padding: '6px 8px' }}>Model</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>调用</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>输入 tok</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>输出 tok</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>cache 读 tok</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>调用成本</th>
                </tr>
              </thead>
              <tbody>
                {aiUsage.byModel.map((m) => (
                  <tr key={m.modelKey ?? JSON.stringify([m.modelName])} style={{ borderTop: '1px solid var(--bd-1)' }}>
                    <td style={{ padding: '6px 8px' }} className="mono">{m.modelLabel ?? m.modelName}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(m.calls)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(m.inputTokens)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(m.outputTokens)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--fg-3)' }} className="mono">{fmtNum(m.cacheReadTokens)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtTinyUsd(m.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          )}
        </Section>
      )}

      <Section title="延迟构成" action={<span style={{fontSize:10,color:'var(--fg-3)'}}>所选日期 · 各阶段样本均值</span>}>
        {overviewLoading ? <LoadingHint /> : latencyBreakdown?.phases?.some(p => p.samples > 0) ? (
          <div>
            <div style={{fontSize:11,color:'var(--fg-3)',marginBottom:8}}>上游耗时包含连接；各阶段样本不同，均值不可相加。</div>
            <DataTable><thead><tr><th>阶段</th><th>平均耗时</th><th>样本数</th></tr></thead><tbody>
              {latencyBreakdown.phases.map(p => <tr key={p.key}><td>{p.label}</td><td>{p.avgMs == null ? '—' : `${p.avgMs} ms`}</td><td>{fmtNum(p.samples)}</td></tr>)}
            </tbody></DataTable>
          </div>
        ) : <div style={{padding:24,color:'var(--fg-3)'}}>所选日期内尚无可用 timing 数据</div>}
      </Section>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, marginBottom: 16 }}>
        <Section
          title={`Endpoint 排行 (${detail?.endpoints?.length || 0})`}
          action={
            <ExportCsvButton
              rows={[...(detail?.endpoints || [])].sort((a, b) => (b.calls - a.calls) || (Number(b.totalCost) - Number(a.totalCost)))}
              filename={`endpoints-${api?.name || 'api'}-${new Date().toISOString().slice(0, 10)}.csv`}
              columns={[
                { key: 'name', label: 'Endpoint' },
                { key: 'method', label: 'Method' },
                { key: 'path', label: 'Path' },
                { key: 'calls', label: 'Calls' },
                // Exact distinct callers over the selected window, matching the table.
                { key: 'uniqueCallers', label: 'Unique callers' },
                { key: 'successRate', label: 'Success rate %' },
                { key: 'totalCost', label: 'Usage cost USD' },
                { key: 'avgLatency', label: 'Avg latency ms' },
              ]}
            />
          }
        >
          {overviewLoading ? (
            <LoadingHint />
          ) : detail?.endpoints?.length ? (
            <DataTable>
              <thead>
                <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                  <th style={{ padding: '6px 8px' }}>Endpoint</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>调用</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }} title="所选窗口内按当前 Key 归属去重">独立调用方</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>成功率</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>调用成本</th>
                  {/* Removed: per-row "筛选↓" trailing column. The same
                      action (filter the call-log table below to this
                      endpoint) is already available via the 端点 dropdown
                      in FilterBar above, so the column was visual noise
                      that didn't add reach. Drill-down navigation is still
                      the primary action — clicking anywhere on the row. */}
                </tr>
              </thead>
              <tbody>
                {/* 排行:按调用量降序(并列再按毛收入),否则 936 个端点按定义顺序排,
                    有流量的端点被埋在后面,前 10 行全是 0。 */}
                {[...detail.endpoints]
                  .sort((a, b) => (b.calls - a.calls) || (Number(b.totalCost) - Number(a.totalCost)))
                  .slice(0, 10)
                  .map((ep) => (
                  <tr
                    key={ep.endpointId}
                    style={{ borderTop: '1px solid var(--bd-1)', cursor: 'pointer' }}
                    onClick={() => navigate('workspace-endpoint', { apiId, endpointId: ep.endpointId })}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-1)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    title="点击查看该 endpoint 的完整分析"
                  >
                    <td style={{ padding: '6px 8px' }}>
                      <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <MethodPill method={ep.method} />
                        <span style={{ color: 'var(--fg-0)' }}>{ep.name}</span>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--fg-3)', marginTop: 2 }} className="mono">
                        {ep.path}
                      </div>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(ep.calls)}</td>
                    {/* uniqueCallers 是所选窗口内精确去重，非日 distinct 累加 */}
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">
                      {ep.uniqueCallers > 0 ? fmtNum(ep.uniqueCallers) : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">
                      {ep.successRate != null ? `${ep.successRate.toFixed(1)}%` : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtTinyUsd(ep.totalCost)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : (
            <EmptyTable cols={4} rows={4} hint="首次调用后这里会显示 endpoint 排行" />
          )}
        </Section>

        <Section title="状态码分布">
          {overviewLoading ? (
            <LoadingHint />
          ) : statusCodes && pieData.some((d) => d.value > 0) ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <PieSlices data={pieData} size={120} />
              <div style={{ fontSize: 11, flex: 1 }}>
                {pieData.map((d) => (
                  <div
                    key={d.label}
                    onClick={() => updateFilter({ statusBucket: d.label })}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
                      cursor: 'pointer', padding: '2px 4px',
                      background: filters.statusBucket === d.label ? 'var(--bg-2)' : 'transparent',
                      borderRadius: 3,
                    }}
                    title="点击筛选日志到此状态段"
                  >
                    <span style={{
                      display: 'inline-block', width: 12, height: 12, background: d.color,
                      borderRadius: 3, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.15)',
                    }} />
                    <span style={{ color: 'var(--fg-1)' }}>{d.label}</span>
                    <span className="mono" style={{ marginLeft: 'auto', color: 'var(--fg-2)' }}>{fmtNum(d.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <EmptyPie size={120} hint="2xx / 3xx /4xx / 5xx" />
              <div style={{ fontSize: 11, color: 'var(--fg-3)', flex: 1 }}>
                首次调用后这里会按状态码拆分
              </div>
            </div>
          )}
        </Section>
      </div>

      {/* C2 — Errors 视图：把 4xx+5xx 按 (statusCode, endpoint) 分组排名 */}
      {errorGroups && errorGroups.totalErrors > 0 && (
        <Section
          title={`错误分组 (${fmtNum(errorGroups.totalErrors)} 次失败 · top ${errorGroups.groups.length}）`}
          action={
            <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>
              点击一行下钻到具体日志 ↓
            </span>
          }
        >
          {errorGroups.byOrigin && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10, fontSize: 11 }}>
              <span style={{ color: 'var(--fg-3)' }}>按责任方：</span>
              {[
                ['UPSTREAM', errorGroups.byOrigin.upstream],
                ['REQUEST', errorGroups.byOrigin.request],
                ['USER', errorGroups.byOrigin.user],
                ['GATEWAY', errorGroups.byOrigin.gateway],
              ].filter(([, n]) => n > 0).map(([o, n]) => (
                <span key={o} style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
                  <OriginBadge origin={o} />
                  <span className="mono" style={{ color: 'var(--fg-2)' }}>{fmtNum(n)}</span>
                </span>
              ))}
              {errorGroups.byOrigin.unknown > 0 && (
                <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center', color: 'var(--fg-4)' }}>
                  未分类 <span className="mono">{fmtNum(errorGroups.byOrigin.unknown)}</span>
                </span>
              )}
              <span style={{ color: 'var(--fg-4)', marginLeft: 4 }}>· 仅「上游」反映你的可靠性，其余为消费者侧</span>
            </div>
          )}
          <DataTable>
            <thead>
              <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                <th style={{ padding: '6px 8px', width: 60 }}>状态</th>
                <th style={{ padding: '6px 8px' }}>Endpoint</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>次数</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>占比</th>
                <th style={{ padding: '6px 8px' }}>最近一次</th>
                <th style={{ padding: '6px 8px', width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {errorGroups.groups.slice(0, 10).map((g, i) => {
                const pct = (g.count / errorGroups.totalErrors) * 100;
                return (
                  <tr
                    key={`${g.statusCode}-${g.endpointId || 'noendpoint'}-${i}`}
                    data-error-status={g.statusCode}
                    onClick={() => {
                      // 一键设置筛选：状态段 + endpoint，下钻到具体日志
                      setFilters((f) => ({
                        ...f,
                        statusBucket: null,
                        statusCode: g.statusCode,
                        endpointId: g.endpointId,
                      }));
                      logsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    style={{ borderTop: '1px solid var(--bd-1)', cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-1)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '6px 8px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                        <StatusCodePill code={g.statusCode} />
                        {g.errorOrigin && <OriginBadge origin={g.errorOrigin} />}
                      </div>
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {g.endpointName ? (
                        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          <MethodPill method={g.method} />
                          <span style={{ color: 'var(--fg-1)' }}>{g.endpointName}</span>
                          <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 10 }}>{g.path}</span>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--fg-3)' }}>(unmapped endpoint)</span>
                      )}
                      {(g.errorCode || g.sampleMessage) && (
                        <div style={{ marginTop: 3, fontSize: 10, color: 'var(--fg-4)', display: 'flex', gap: 6, alignItems: 'baseline' }}>
                          {g.errorCode && <span className="mono" style={{ color: 'var(--fg-3)' }}>{g.errorCode}</span>}
                          {g.sampleMessage && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }} title={g.sampleMessage}>{g.sampleMessage}</span>}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtNum(g.count)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{pct.toFixed(1)}%</td>
                    <td style={{ padding: '6px 8px', color: 'var(--fg-3)' }} className="mono">
                      {new Date(g.lastSeen).toLocaleString()}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--fg-3)' }}>→</td>
                  </tr>
                );
              })}
            </tbody>
          </DataTable>
        </Section>
      )}

      </>}

      <div ref={logsAnchorRef} />
      <Section
        title="调用日志"
        action={
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>
              点击行查看完整 headers / body
            </span>
            <ExportCsvButton
              rows={sortedLogs}
              filename={`traffic-logs-${api?.name || 'api'}-${new Date().toISOString().slice(0, 10)}.csv`}
              columns={[
                { key: 'createdAt', label: 'Time', transform: (v) => new Date(v).toISOString() },
                { key: 'method', label: 'Method' },
                { key: 'statusCode', label: 'Status' },
                { key: 'endpointId', label: 'Endpoint', transform: (v) => epLookup.get(v)?.name || '' },
                { key: 'callerHandle', label: 'Caller' },
                { key: 'responseTime', label: 'Latency ms' },
                { key: 'cost', label: 'Cost USD' },
                { key: 'country', label: 'Country' },
                { key: 'requestUrl', label: 'URL' },
                { key: 'id', label: 'Log ID' },
              ]}
            />
          </div>
        }
      >
        {logsError && <div role="alert">{logsError} <button onClick={() => fetchLogs({append: logs.length > 0, cursor: logCursor})}>重试日志</button></div>}
        {effectiveDrill && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              marginBottom: 8,
              background: 'rgba(91, 141, 239, 0.1)',
              border: '1px solid rgba(91, 141, 239, 0.3)',
              borderRadius: 6,
              fontSize: 11,
              color: 'var(--fg-1)',
            }}
          >
            <span>🔍 已下钻到时段</span>
            <span className="mono" style={{ color: 'var(--fg-0)' }}>{effectiveDrill.label}</span>
            <button
              type="button"
              onClick={() => setDrillWindow(null)}
              style={{
                marginLeft: 'auto',
                padding: '2px 8px',
                background: 'transparent',
                border: '1px solid var(--bd-1)',
                color: 'var(--fg-2)',
                borderRadius: 4,
                fontSize: 10,
                cursor: 'pointer',
              }}
            >返回 {range} 全窗口 ×</button>
          </div>
        )}
        <FilterBar>
          <Select
            label="端点"
            value={filters.endpointId}
            onChange={(v) => updateFilter({ endpointId: v })}
            options={endpointOptions}
            placeholder={`全部 (${endpointOptions.length})`}
            width={200}
          />
          <PillGroup
            label="状态"
            value={filters.statusBucket}
            options={STATUS_BUCKET_OPTIONS}
            onChange={(v) => updateFilter({ statusBucket: v, statusCode: null })}
          />
          <PillGroup
            label="方法"
            value={filters.method}
            options={METHOD_OPTIONS}
            onChange={(v) => updateFilter({ method: v })}
          />
          {countryOptions.length > 0 && (
            <Select
              label="地区"
              value={filters.country}
              onChange={(v) => updateFilter({ country: v })}
              options={countryOptions}
              placeholder={`全部 (${countryOptions.length})`}
              width={110}
            />
          )}
          <CallerFilter apiId={apiId} bounds={bounds} value={filters.callerId} onChange={v => updateFilter({ callerId: v })} logs={logs} />
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="URL 模糊 / log id 精确"
            width={220}
          />
          {hasFilters && (
            <button
              type="button"
              onClick={resetFilters}
              style={{
                padding: '5px 10px', border: '1px solid var(--bd-1)',
                background: 'transparent', borderRadius: 6,
                color: 'var(--fg-3)', fontSize: 11, cursor: 'pointer',
              }}
            >清除筛选 ×</button>
          )}
        </FilterBar>

        {logsLoading && logs.length === 0 ? (
          <LoadingHint />
        ) : sortedLogs.length === 0 ? (
          hasFilters ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-3)', fontSize: 12 }}>
              当前筛选下没有匹配的日志 · <button type="button" onClick={resetFilters} style={{ background: 'none', border: 'none', color: 'var(--fg-1)', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, padding: 0 }}>清除筛选</button>
            </div>
          ) : (
            <EmptyTable cols={8} rows={4} hint={logsError ? '调用日志暂不可用' : '当前保留的原始日志中没有匹配记录'} subHint="点击行可看完整 headers / body" />
          )
        ) : (
          <>
            <DataTable>
              <thead>
                <tr style={{ color: 'var(--fg-3)', textAlign: 'left', fontWeight: 400 }}>
                  <SortHeader sortKey="createdAt" currentSort={logSort.key} currentDir={logSort.dir} onSort={onSort}>
                    时间
                  </SortHeader>
                  <th style={{ padding: '6px 8px' }}>方法</th>
                  <SortHeader sortKey="statusCode" currentSort={logSort.key} currentDir={logSort.dir} onSort={onSort}>
                    状态
                  </SortHeader>
                  <th style={{ padding: '6px 8px' }}>端点</th>
                  <th style={{ padding: '6px 8px' }}>调用方</th>
                  <SortHeader sortKey="responseTime" currentSort={logSort.key} currentDir={logSort.dir} onSort={onSort} align="right">
                    延迟
                  </SortHeader>
                  <SortHeader sortKey="cost" currentSort={logSort.key} currentDir={logSort.dir} onSort={onSort} align="right">
                    花费
                  </SortHeader>
                  <th style={{ padding: '6px 8px' }}>地区</th>
                </tr>
              </thead>
              <tbody>
                {sortedLogs.map((log) => {
                  const ep = epLookup.get(log.endpointId);
                  return (
                    <tr
                      key={log.id}
                      data-log-id={log.id}
                      onClick={() => setActiveLog(log)}
                      style={{ borderTop: '1px solid var(--bd-1)', cursor: 'pointer' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-1)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{ padding: '6px 8px', color: 'var(--fg-2)', whiteSpace: 'nowrap' }} className="mono">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <MethodPill method={log.method} />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <StatusCodePill code={log.statusCode} />
                      </td>
                      <td style={{ padding: '6px 8px', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ep ? (
                          <span style={{ color: 'var(--fg-1)' }} title={`${ep.method} ${ep.path}`}>{ep.name}</span>
                        ) : (
                          <span className="mono" style={{ color: 'var(--fg-3)' }} title={log.requestUrl}>{log.requestUrl?.split('?')[0]?.slice(-32) || '—'}</span>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px' }} className="mono">
                        {log.callerRef ? (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('workspace-caller', { apiId, callerId: log.callerRef });
                            }}
                            title={`${log.callerHandle} · 点击 drill-down`}
                            style={{ color: 'var(--fg-2)', cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}
                          >
                            {log.callerHandle}
                            {log.callerType === 'oauth_user' && (
                              <span style={{ marginLeft: 4, fontSize: 9 }}>👤</span>
                            )}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{log.responseTime}ms</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }} className="mono">{fmtTinyUsd(log.cost)}</td>
                      <td style={{ padding: '6px 8px' }}><CountryCell iso={log.country} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </DataTable>
            <TableFooter
              hasMore={logHasMore}
              loading={logsLoading}
              totalLoaded={sortedLogs.length}
              onLoadMore={() => fetchLogs({ append: true, cursor: logCursor })}
            />
          </>
        )}
      </Section>

      {activeLog && (
        <LogDetailDrawer key={activeLog.id} log={activeLog} onClose={() => setActiveLog(null)} epLookup={epLookup} />
      )}
    </div>
  );
};

// 简易日志详情抽屉
const LogDetailDrawer = ({ log, onClose, epLookup }) => {
  const [full, setFull] = useState(null);
  useEffect(() => {
    let cancelled = false;
    XApi.providerDashboardService.logDetail(log.id)
      .then(d => { if (!cancelled) setFull(d || { ...log }); })
      .catch(() => { if (!cancelled) setFull({ ...log }); });
    return () => { cancelled = true; };
  }, [log.id]);
  const ep = epLookup?.get(log.endpointId);
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 30, display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 92vw)', background: 'var(--bg-0)',
          padding: '20px 24px', overflowY: 'auto', borderLeft: '1px solid var(--bd-1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>调用详情</h3>
          <button
            type="button" onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: 'var(--fg-2)', cursor: 'pointer', fontSize: 16 }}
          >×</button>
        </div>
        {full == null ? (
          <MetaSkeleton />
        ) : (
          <div style={{ fontSize: 12, color: 'var(--fg-1)', lineHeight: 1.6 }}>
            <DetailRow label="时间" value={new Date(full.createdAt).toLocaleString()} />
            {ep && <DetailRow label="端点" value={<span><MethodPill method={ep.method} /> <span style={{ marginLeft: 6 }}>{ep.name}</span> <span className="mono" style={{ color: 'var(--fg-3)', marginLeft: 6 }}>{ep.path}</span></span>} />}
            <DetailRow label="URL" value={<span className="mono">{full.requestUrl}</span>} />
            <DetailRow label="方法" value={<MethodPill method={full.method} />} />
            <DetailRow label="状态码" value={<StatusCodePill code={full.statusCode} />} />
            <DetailRow label="延迟" value={`${full.responseTime} ms`} />
            <DetailRow label="花费" value={fmtTinyUsd(full.cost)} />
            <DetailRow label="调用方" value={full.callerHandle ? <span className="mono">{full.callerHandle}</span> : '—'} />
            <DetailRow label="国家" value={<CountryCell iso={full.country} />} />
            {full.modelName && <DetailRow label="模型" value={full.modelName} />}
            {full.totalTokens != null && <DetailRow label="Tokens" value={`${full.totalTokens} (in ${full.inputTokens}/out ${full.outputTokens})`} />}
            {full.upstreamLatencyMs != null && <DetailRow label="上游延迟" value={`${full.upstreamLatencyMs} ms`} />}
            {/* 方案A(纯 R2)：req/resp 完整体只在 R2，抽屉打开即自动加载渲染 */}
            {full.hasFullLog ? (
              <FullResponseSection logId={log.id} />
            ) : (
              <div style={{ marginTop: 12, padding: 10, background: 'var(--bg-1)', borderRadius: 4, color: 'var(--fg-3)' }}>
                此调用无完整归档（未捕获 / 关闭了详细日志 / R2 未启用 / 已超保留期被清理）。
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 12, padding: '5px 0', borderBottom: '1px solid var(--bd-1)' }}>
    <div style={{ width: 80, color: 'var(--fg-3)' }}>{label}</div>
    <div style={{ flex: 1, wordBreak: 'break-all' }}>{value}</div>
  </div>
);

// 骨架占位：复用全局 .skeleton shimmer class（与 api-detail / Console 视觉一致）。
const Skel = ({ w = '100%', h = 12, style }) => (
  <span className="skeleton" style={{ display: 'inline-block', width: w, height: h, borderRadius: 4, ...style }} />
);

// 元信息区骨架（匹配 DetailRow 的 label|value 两栏）。
const MetaSkeleton = () => (
  <div style={{ fontSize: 12 }}>
    {[64, 50, 72, 38, 44, 56, 48, 60].map((vw, i) => (
      <div key={i} style={{ display: 'flex', gap: 12, padding: '5px 0', borderBottom: '1px solid var(--bd-1)' }}>
        <Skel w={56} h={11} />
        <Skel w={`${vw}%`} h={11} />
      </div>
    ))}
  </div>
);

// 完整体区骨架（匹配 CodeBlock：小标题 + 灰底多行）。
const BodyBlockSkel = ({ lines = 4 }) => (
  <div style={{ marginTop: 12 }}>
    <Skel w={72} h={11} style={{ marginBottom: 6 }} />
    <div style={{ background: 'var(--bg-1)', padding: 10, borderRadius: 4, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skel key={i} w={`${92 - i * 11}%`} h={10} />
      ))}
    </div>
  </div>
);

// 完整响应（R2）加载中的骨架：标题 + 四块（请求头/体、响应头/体）。
const FullResponseSkeleton = () => (
  <div style={{ marginTop: 14, borderTop: '1px solid var(--bd-1)', paddingTop: 10 }}>
    <Skel w={140} h={11} style={{ marginBottom: 8 }} />
    <BodyBlockSkel lines={2} />
    <BodyBlockSkel lines={3} />
    <BodyBlockSkel lines={2} />
    <BodyBlockSkel lines={5} />
  </div>
);

// 请求/响应 头与体的展示块。
// - JSON 内容自动 pretty-print + 语法高亮(JsonViewer / formatJson 兜底原文)。
// - 右上角:一键复制 + 放大(在大弹窗 JsonModal 里全屏查看,适合长 body)。
const CodeBlock = ({ label, value }) => {
  const [open, setOpen] = useState(false);
  const [copied, copy] = XPrim.useCopy();
  const text = XPrim.formatJson(value);
  return (
    <div style={{ marginTop: 12 }}>
      <div className="row between" style={{ marginBottom: 4, alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{label}</span>
        <div className="row" style={{ gap: 2 }}>
          <button type="button" className="btn btn-sm btn-ghost btn-icon" title={copied ? '已复制' : '复制'} onClick={() => copy(text)}>
            {copied ? <XIcons.IconCheck size={12} /> : <XIcons.IconCopy size={12} />}
          </button>
          <button type="button" className="btn btn-sm btn-ghost btn-icon" title="放大查看" onClick={() => setOpen(true)}>
            <XIcons.IconMaximize size={12} />
          </button>
        </div>
      </div>
      <XPrim.JsonViewer
        value={value}
        maxHeight={240}
        style={{
          background: 'var(--bg-1)', padding: 10, borderRadius: 4, fontSize: 11,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}
      />
      <XPrim.JsonModal open={open} onClose={() => setOpen(false)} title={label} value={value} />
    </div>
  );
};

// R2 归档对象是 gzip 的 {meta,request,response} JSON。浏览器对带
// Content-Encoding: gzip 的响应通常透明解压；这里兜底用原生 DecompressionStream
// 处理「拿到的还是 gzip 字节」的情况（无第三方依赖）。
async function gunzipIfNeeded(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);
  const isGzip = bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
  if (isGzip) {
    if (typeof DecompressionStream === 'undefined') {
      throw new Error('当前浏览器不支持解压（DecompressionStream），请升级到较新版本的 Chrome/Edge/Safari/Firefox。');
    }
    const ds = new DecompressionStream('gzip');
    const stream = new Blob([bytes]).stream().pipeThrough(ds);
    return await new Response(stream).text();
  }
  return new TextDecoder().decode(bytes);
}

const FULL_RESP_REASON = {
  no_archive: '此调用没有完整归档（旧记录，或归档功能当时未启用）。',
  storage_disabled: '归档存储未启用。',
  unavailable: '完整归档暂不可用。',
  not_found: '日志不存在或无权限。',
};

// 完整响应（R2 归档）：方案A 后 detail 无 DB 预览，抽屉打开即自动拉 R2 完整体。
// 换 5min presigned URL → fetch → 解压 → 渲染。LogDetailDrawer 用 key={log.id}
// 挂载，切换日志会 remount → 自动重新加载。
const FullResponseSection = ({ logId }) => {
  const [state, setState] = useState('loading'); // loading|done|error
  const [payload, setPayload] = useState(null);
  const [msg, setMsg] = useState('');
  const load = async () => {
    setState('loading');
    setMsg('');
    try {
      const meta = await XApi.providerDashboardService.logFullResponse(logId);
      if (!meta || !meta.available) {
        setState('error');
        setMsg(FULL_RESP_REASON[meta?.reason] || '完整响应不可用。');
        return;
      }
      const res = await fetch(meta.url);
      if (!res.ok) throw new Error(`对象不存在或已过期 (${res.status})`);
      const text = await gunzipIfNeeded(await res.arrayBuffer());
      setPayload(JSON.parse(text));
      setState('done');
    } catch (e) {
      setState('error');
      setMsg((e && e.message) || '加载失败（可能是存储跨域未配置或链接已过期）。');
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === 'loading') return <FullResponseSkeleton />;
  if (state === 'error') {
    return (
      <div style={{ marginTop: 14 }}>
        <div style={{ padding: 10, background: 'var(--bg-1)', borderRadius: 4, color: 'var(--fg-3)' }}>
          {msg}
        </div>
        <button type="button" className="btn btn-sm btn-ghost" style={{ marginTop: 8 }} onClick={load}>
          重试
        </button>
      </div>
    );
  }
  // done
  const req = payload?.request || {};
  const resp = payload?.response || {};
  const truncated = payload?.meta?.truncated;
  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--bd-1)', paddingTop: 10 }}>
      <div style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 4 }}>
        完整体（R2 归档{truncated ? '，已达大小上限被截断' : ''}）
      </div>
      {req.headers && <CodeBlock label="完整请求头" value={req.headers} />}
      {req.body != null && <CodeBlock label="完整请求体" value={req.body} />}
      {resp.headers && <CodeBlock label="完整响应头" value={resp.headers} />}
      {resp.body != null && <CodeBlock label="完整响应体" value={resp.body} />}
    </div>
  );
};
